import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  NotificationHubService,
  NotificationItem,
  NotificationStats
} from 'src/app/providers/notification-hub/notification-hub.service';
import { AttachmentPreviewDialogComponent } from '../attachment-preview-dialog/attachment-preview-dialog.component';
import { NotificationAttachment } from 'src/app/shared/models/notification-attachment.model';
import { NotificationAttachmentService } from 'src/app/shared/services/notification-attachment.service';

interface CategoryTab {
  key: string;
  label: string;
}

@Component({
  selector: 'app-notification-hub',
  templateUrl: './notification-hub.component.html',
  styleUrls: ['./notification-hub.component.scss']
})
export class NotificationHubComponent implements OnInit, OnDestroy {
  categories: CategoryTab[] = [
    { key: 'all', label: 'All' },
    { key: 'general', label: 'General' },
    { key: 'alert', label: 'Alerts' },
    { key: 'announcement', label: 'Announcements' },
    { key: 'reminder', label: 'Reminders' }
  ];

  activeCategory = 'all';
  items: NotificationItem[] = [];
  filteredItems: NotificationItem[] = [];
  stats: NotificationStats = { total: 0, unread: 0, critical: 0, byCategory: {} };
  loading = false;
  loadingMore = false;
  hasMore = false;

  private destroy$ = new Subject<void>();
  private tabUnreadCache: Record<string, number> = {};

  constructor(
    public hub: NotificationHubService,
    private ngbModal: NgbModal,
    private attachmentService: NotificationAttachmentService
  ) {}

  ngOnInit(): void {
    this.hub.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.items = items;
      this.applyFilters();
    });
    this.hub.loading$.pipe(takeUntil(this.destroy$)).subscribe((l) => (this.loading = l));
    this.hub.loadingMore$.pipe(takeUntil(this.destroy$)).subscribe((l) => (this.loadingMore = l));
    this.hub.hasMore$.pipe(takeUntil(this.destroy$)).subscribe((m) => (this.hasMore = m));
    // Navbar owns the initial load to avoid a double REST fetch.
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setCategory(key: string): void {
    this.activeCategory = key;
    this.hub.refresh(key === 'all' ? undefined : key);
  }

  tabUnreadCount(key: string): number {
    return this.tabUnreadCache[key] || 0;
  }

  loadMore(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.hub.loadMore(this.activeCategory === 'all' ? undefined : this.activeCategory);
  }

  markRead(item: NotificationItem, event?: Event): void {
    event?.stopPropagation();
    this.hub.markRead(item);
    this.applyFilters();
  }

  onCardClick(item: NotificationItem): void {
    this.markRead(item);
  }

  getAttachments(item: NotificationItem): NotificationAttachment[] {
    return this.hub.getAttachments(item);
  }

  openAttachmentPreview(attachment: NotificationAttachment, event?: Event): void {
    event?.stopPropagation();
    const modalRef = this.ngbModal.open(AttachmentPreviewDialogComponent, {
      size: 'xl',
      windowClass: 'attachment-preview-modal',
      backdrop: true,
      keyboard: true,
      centered: true
    });
    modalRef.componentInstance.attachment = attachment;
  }

  downloadAttachmentFile(attachment: NotificationAttachment, event?: Event): void {
    event?.stopPropagation();
    this.attachmentService.downloadFile(attachment);
  }

  markAllRead(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const cat = this.activeCategory === 'all' ? undefined : this.activeCategory;
    this.hub.markAllRead(cat);
    this.applyFilters();
  }

  refresh(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.hub.refresh(this.activeCategory === 'all' ? undefined : this.activeCategory);
  }

  categoryLabel(key: string): string {
    if (key === 'info') { return 'General'; }
    const tab = this.categories.find((c) => c.key === key);
    if (tab) { return tab.label; }
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  categoryIcon(category: string): string {
    const map: Record<string, string> = {
      general: 'info',
      info: 'info',
      alert: 'alert-triangle',
      announcement: 'volume-2',
      reminder: 'clock'
    };
    return map[category] || 'bell';
  }

  priorityLabel(priority: string): string {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  }

  relativeTime(date: Date): string {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  }

  trackById(_index: number, item: NotificationItem): string {
    return item.id;
  }

  private applyFilters(): void {
    const list = this.hub.filterItems(this.items, '', this.activeCategory);
    this.filteredItems = list;
    this.stats = this.hub.getStats(list);
    const badgeSource = this.hub.getBadgeItems();
    this.tabUnreadCache = {
      all: badgeSource.filter((i) => !i.read).length,
      general: badgeSource.filter((i) => !i.read && i.category === 'general').length,
      alert: badgeSource.filter((i) => !i.read && i.category === 'alert').length,
      announcement: badgeSource.filter((i) => !i.read && i.category === 'announcement').length,
      reminder: badgeSource.filter((i) => !i.read && i.category === 'reminder').length
    };
  }
}
